import { qs, delegate } from '../lib/dom.js';
import { t } from '../lib/i18n.js';

// Single page size for the whole catalog front end. MUST match the server's
// atpl_catalog_per_page (inc/rest-api.php) - catalog.js used to keep its own
// copy at 15 for the range label, which is why client-side pagination printed
// "16-30" over a page of 12 cars. Exported so there is one value, not two.
export const PER_PAGE = 12;

export function paginate(items, page) {
  const totalPages = Math.max(1, Math.ceil(items.length / PER_PAGE));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * PER_PAGE;
  return {
    page: safePage,
    totalPages,
    items: items.slice(start, start + PER_PAGE),
  };
}

// Render as real <a> anchors (crawlable, right-click / new-tab friendly) rather
// than <button>s. `hrefFor(n)` supplies the canonical URL for page n; the
// active page and disabled prev/next render as inert <span>s (no href, no
// data-page) so the click handler ignores them. Behaviour is unchanged - a
// normal click is still intercepted and handled via AJAX in bindPagination().
export function renderPagination(container, page, totalPages, hrefFor) {
  if (!container) return;
  // The container is always in the DOM (server renders it even for 1 page, see
  // template-parts/catalog/pagination.php); toggle `hidden` so an empty nav
  // doesn't take space, and reveal it again when a filter change adds pages.
  if (totalPages <= 1) { container.innerHTML = ''; container.hidden = true; return; }

  const href = (n) => (typeof hrefFor === 'function' ? hrefFor(n) : '#');
  const out = [];

  // Previous
  if (page <= 1) {
    out.push(`<span class="ap-pagination__btn is-disabled" aria-disabled="true" aria-label="${t('page_prev', 'Pagina anterioară')}">‹</span>`);
  } else {
    out.push(`<a class="ap-pagination__btn" href="${href(page - 1)}" data-page="${page - 1}" rel="prev" aria-label="${t('page_prev', 'Pagina anterioară')}">‹</a>`);
  }

  // Page numbers - show first, last, and 1 around current
  const pages = new Set([1, totalPages, page, page - 1, page + 1]);
  let prev = 0;
  for (let i = 1; i <= totalPages; i++) {
    if (!pages.has(i)) continue;
    if (prev && i - prev > 1) out.push(`<span class="ap-pagination__ellipsis">…</span>`);
    if (i === page) {
      out.push(`<span class="ap-pagination__btn is-active" aria-current="page">${i}</span>`);
    } else {
      out.push(`<a class="ap-pagination__btn" href="${href(i)}" data-page="${i}">${i}</a>`);
    }
    prev = i;
  }

  // Next
  if (page >= totalPages) {
    out.push(`<span class="ap-pagination__btn is-disabled" aria-disabled="true" aria-label="${t('page_next', 'Pagina următoare')}">›</span>`);
  } else {
    out.push(`<a class="ap-pagination__btn" href="${href(page + 1)}" data-page="${page + 1}" rel="next" aria-label="${t('page_next', 'Pagina următoare')}">›</a>`);
  }

  container.innerHTML = out.join('');
  container.hidden = false;
}

export function bindPagination(container, onChange) {
  if (!container) return;
  delegate(container, 'click', '.ap-pagination__btn[data-page]', (e, btn) => {
    // Let modified clicks (Cmd/Ctrl/Shift/Alt + click) fall through to the
    // real anchor href so users can open a page in a new tab - normal clicks
    // are still handled in-place via AJAX below (unchanged behaviour).
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    const page = Number(btn.dataset.page);
    if (Number.isFinite(page)) {
      onChange(page);
      window.scrollTo({ top: container.parentElement.offsetTop - 80, behavior: 'smooth' });
    }
  });
}
